import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { StateService } from '../../../services/state.service';
import * as Sentry from '@sentry/angular';
import { BewerberAdapter } from '../../../adapter/bewerber.adapter';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { TauresMessageModule } from '@taures/taures-components/taures-message';
import { VerificationResponseDto } from '../../../adapter/dto/verification-response.dto';

export enum PdfDocumentType {
  DATENSCHUTZEINWILLIGUNG = 'datenschutzeinwilligung',
  VERSCHWIEGENHEITSVERPFLICHTUNG = 'verschwiegenheitsverpflichtung'
}

export type PdfData = { pdfDocumentType: PdfDocumentType; pdfUrl: string };

@Component({
  selector: 'app-pdf-view-accept',
  templateUrl: './pdf-view-accept.component.html',
  styleUrls: ['./pdf-view-accept.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, Checkbox, Button, Skeleton, TauresMessageModule],
  standalone: true
})
export class PdfViewAcceptComponent {
  private readonly destroyed: DestroyRef = inject(DestroyRef);
  protected control: FormControl<boolean | null> = new FormControl<boolean>(false, { validators: [Validators.requiredTrue] });

  private pdfUrlValue: string = '';
  private pdfDocumentType!: PdfDocumentType;
  private trustedURL!: SafeResourceUrl;

  public constructor(
    private readonly sanitizer: DomSanitizer,
    public stateService: StateService,
    private readonly route: ActivatedRoute,
    private readonly bewerberService: BewerberAdapter,
    private readonly router: Router
  ) {
    this.route.data.pipe(takeUntilDestroyed(this.destroyed)).subscribe((data: Data): void => {
      const typed: PdfData = data as PdfData;
      this.pdfDocumentType = typed.pdfDocumentType;
      this.pdfUrl = typed.pdfUrl;
    });
  }

  protected get pdfUrl(): string {
    return this.pdfUrlValue;
  }

  @Input()
  protected set pdfUrl(value: string) {
    this.pdfUrlValue = value;
    this.trustedURL = this.sanitizer.bypassSecurityTrustResourceUrl(`/api/vp/${this.pdfUrlValue}`);
  }

  protected get dataURL(): SafeResourceUrl {
    return this.trustedURL;
  }

  protected get title(): string {
    if (this.pdfUrlValue === '') {
      return '';
    }
    return this.pdfUrlValue.substring(0, this.pdfUrlValue.lastIndexOf('.')).replace(/^[a-zA-z]|\s(.)/gi, (L: string): string => L.toUpperCase());
  }

  protected accept(): void {
    if (this.pdfUrlValue === '' || !this.stateService.hasValidTokenAndBirthdate()) {
      this.stateService.setHasError(true);
      return;
    }

    const token: string = this.stateService.getToken()!;
    const birthdate: Date = this.stateService.getBirthdate()!;

    this.stateService.setIsLoading(true);
    this.stateService.setHasError(false);

    this.bewerberService
      .accept(this.pdfUrlValue, token, birthdate)
      .pipe(take(1))
      .subscribe({
        next: (): void => {
          this.stateService.setIsLoading(false);
          this.stateService.setHasError(false);

          const currentResult: VerificationResponseDto | null = this.stateService.getVerificationResponse();
          if (currentResult !== null) {
            switch (this.pdfDocumentType) {
              case PdfDocumentType.DATENSCHUTZEINWILLIGUNG:
                this.stateService.datenschutzeinwilligungAkzeptiert();
                break;
              case PdfDocumentType.VERSCHWIEGENHEITSVERPFLICHTUNG:
                this.stateService.verschwiegenheitsverpflichtungAkzeptiert();
                break;
            }
          }

          this.router.navigate(['/vp', token, 'landing'], { replaceUrl: true });
        },
        error: (err: Error): void => {
          Sentry.captureEvent(err);
          this.stateService.setIsLoading(false);
          this.stateService.setHasError(true);
        }
      });
  }
}
